import { clinicalPatient, seedEntries, seedReviews, visits } from '../data/clinicalDemo'
import type { ClinicalAction, ClinicalHistoryEvent, ClinicalState, VisitReviewOutcome } from '../types/clinicalDemo'
import { formatId, nowIso } from './demoStorage'

export const clinicalStorageKey = 'noetivra.demo.clinical.v1'

export type VisitStatus = 'unreviewed' | VisitReviewOutcome

export const visitStatusLabels: Record<VisitStatus, string> = {
  unreviewed: 'Not yet reviewed',
  'clarification-requested': 'Clarification requested',
  reviewed: 'Reviewed',
}

export const visitById = (id: string) => visits.find((visit) => visit.id === id)

export function visitStatus(state: ClinicalState, visitId: string): VisitStatus {
  const reviews = state.reviews.filter((review) => review.visitId === visitId)
  return reviews[reviews.length - 1]?.outcome ?? 'unreviewed'
}

/** Once a visit has been reviewed, later entries are appended as addenda. */
export const hasBeenReviewed = (state: ClinicalState, visitId: string) =>
  state.reviews.some((review) => review.visitId === visitId && review.outcome === 'reviewed')

export function createInitialClinicalState(): ClinicalState {
  const history: ClinicalHistoryEvent[] = [
    ...seedEntries.map((entry, index) => ({
      id: formatId('EVT', index + 1),
      at: entry.at,
      action: `${entry.id} recorded for ${entry.visitId} · sample record`,
      visitId: entry.visitId,
    })),
    {
      id: formatId('EVT', seedEntries.length + 1),
      at: seedReviews[0].at,
      action: `${seedReviews[0].id}: VIS-01 documentation reviewed · sample record`,
      visitId: 'VIS-01',
    },
  ].reverse()

  return {
    entries: seedEntries.map((entry) => ({ ...entry })),
    reviews: seedReviews.map((review) => ({ ...review })),
    history,
    counters: { entry: seedEntries.length, review: seedReviews.length, event: history.length },
    ui: {
      workspace: null,
      lastSection: { clinician: 'overview', review: 'overview' },
      selectedVisitId: 'VIS-02',
      entryDrafts: {},
      reviewDrafts: {},
    },
  }
}

export function clinicalReducer(state: ClinicalState, action: ClinicalAction): ClinicalState {
  const ui = state.ui
  const log = (current: ClinicalState, text: string, at: string, visitId: string): ClinicalState => {
    const next = current.counters.event + 1
    return {
      ...current,
      history: [{ id: formatId('EVT', next), at, action: text, visitId }, ...current.history],
      counters: { ...current.counters, event: next },
    }
  }

  switch (action.type) {
    case 'enterWorkspace':
      return { ...state, ui: { ...ui, workspace: action.workspace } }
    case 'setSection':
      if (!ui.workspace) return state
      return { ...state, ui: { ...ui, lastSection: { ...ui.lastSection, [ui.workspace]: action.section } } }
    case 'selectVisit': {
      const lastSection = action.section && ui.workspace ? { ...ui.lastSection, [ui.workspace]: action.section } : ui.lastSection
      return { ...state, ui: { ...ui, selectedVisitId: action.visitId, lastSection } }
    }
    case 'setEntryDraft':
      return { ...state, ui: { ...ui, entryDrafts: { ...ui.entryDrafts, [action.visitId]: action.draft } } }
    case 'addEntry': {
      const draft = ui.entryDrafts[action.visitId]
      if (!draft?.category || !draft.text.trim() || !visitById(action.visitId)) return state
      const isAddendum = hasBeenReviewed(state, action.visitId)
      const at = nowIso()
      const number = state.counters.entry + 1
      const id = formatId('ENT', number)
      const entryDrafts = { ...ui.entryDrafts }
      delete entryDrafts[action.visitId]
      return log(
        {
          ...state,
          entries: [
            ...state.entries,
            {
              id,
              visitId: action.visitId,
              category: draft.category,
              text: draft.text.trim(),
              authorRole: 'Clinician',
              at,
              isAddendum,
              addendumTo: isAddendum && draft.addendumTo ? draft.addendumTo : undefined,
            },
          ],
          counters: { ...state.counters, entry: number },
          ui: { ...ui, entryDrafts },
        },
        `${isAddendum ? 'Addendum' : 'Entry'} ${id} recorded for ${action.visitId}${isAddendum && draft.addendumTo ? ` (to ${draft.addendumTo})` : ''}`,
        at,
        action.visitId,
      )
    }
    case 'setReviewDraft':
      return { ...state, ui: { ...ui, reviewDrafts: { ...ui.reviewDrafts, [action.visitId]: action.draft } } }
    case 'submitReview': {
      const draft = ui.reviewDrafts[action.visitId]
      if (!draft?.outcome || !draft.explanation.trim()) return state
      const at = nowIso()
      const number = state.counters.review + 1
      const id = formatId('CRV', number)
      const entryIds = state.entries.filter((entry) => entry.visitId === action.visitId).map((entry) => entry.id)
      const reviewDrafts = { ...ui.reviewDrafts }
      delete reviewDrafts[action.visitId]
      return log(
        {
          ...state,
          reviews: [
            ...state.reviews,
            { id, visitId: action.visitId, outcome: draft.outcome, explanation: draft.explanation.trim(), entryIds, reviewerRole: 'Authorized reviewer', at },
          ],
          counters: { ...state.counters, review: number },
          ui: { ...ui, reviewDrafts },
        },
        `${id}: ${action.visitId} ${draft.outcome === 'reviewed' ? 'documentation reviewed' : 'clarification requested'} (${entryIds.length} entries)`,
        at,
        action.visitId,
      )
    }
    default:
      return state
  }
}

export function clinicalExport(state: ClinicalState) {
  return {
    exportedAt: nowIso(),
    domain: 'clinical',
    notice: 'Fictional documentation example. Not a diagnosis, clinical guideline or medical reference.',
    patient: clinicalPatient,
    visits,
    entries: state.entries,
    documentationReviews: state.reviews,
    history: state.history,
  }
}
